import type { RulesAddSubFlag } from './rulesAddSubProblems';
import type { RulesPanelCopy } from './rulesPanelCopy';

/**
 * Rules-panel copy for the thirteen `VIFRulesAddSub` variants.
 *
 * In the CreateJS build this text does not exist as text: `rulesclip`'s
 * `ruleHdr` / `ruleTxt` sub-symbols are selected by frame
 * (`rulesBoardEngine.ts:210-213`) and their content is outlined vector artwork
 * - 157 `cjs.Shape` path blobs in `lib.ruletext` alone. Nothing in the
 * codebase carries the strings.
 *
 * These were recovered by mounting the composition headless and reading each
 * frame, so the DOM board renders real, selectable, screen-reader-accessible
 * text instead of vector outlines.
 *
 * Frame indexing note: `UIUtil.setFrame(mc, n)` calls `gotoAndStop(n - 1)`
 * (`packages/core/animation/src/ui.ts:56`) and the engine passes
 * `gameFlag + 1`, so the rendered frame index equals `gameFlag`. Reading the
 * frames off by one attributes every rule to the wrong variant - flag 6, the
 * first subtraction variant, is the giveaway because its header switches to
 * "VIFs Subtract".
 *
 * Only `ruleHdr` and `ruleTxt` are transcribed. The `equatn` and `examples`
 * sub-symbols carry a second "Part 2" block, but they sit at x = -764 inside a
 * clip positioned at x = 676 on an 800-wide stage, so they land off-screen and
 * no code ever moves them. Players never see them.
 */

export const RULES_ADD_SUB_COPY: Record<RulesAddSubFlag, RulesPanelCopy> = {
  0: {
    title: 'VIFs Add',
    subtitle: 'ZERO Rule',
    body: 'Add 0 to a number and it stays the same.',
    operation: 'add',
  },
  1: {
    title: 'VIFs Add',
    subtitle: 'ONE Rule',
    body: 'Add 1 to a number and the sum is the next Number on the number line.',
    operation: 'add',
  },
  2: {
    title: 'VIFs Add',
    subtitle: 'MIRROR Rule',
    body: 'Add the numbers in any order and the sum is the same.',
    operation: 'add',
  },
  3: {
    title: 'VIFs Add',
    subtitle: 'SUM > 10',
    body: 'Add two single-digit numbers whose sum > 10',
    operation: 'add',
  },
  4: {
    title: 'VIFs Add',
    subtitle: 'Tens Place',
    body: 'Add two double-digit numbers in the tens place',
    operation: 'add',
  },
  5: {
    title: 'VIFs Add',
    subtitle: 'Double Digits',
    body: 'Add any two double-digit numbers',
    operation: 'add',
  },
  6: {
    title: 'VIFs Subtract',
    subtitle: 'ZERO Rule',
    body: 'Part 1: A number minus 0 stays the same',
    example: '6 - 0 = 6',
    operation: 'subtract',
  },
  7: {
    // Transcribed as shipped. The body says "minus itself" while the example
    // subtracts one; the artwork has read that way since the Flash build, so
    // it is reproduced rather than corrected. Flagged for the client.
    title: 'VIFs Subtract',
    subtitle: 'ONE Rule',
    body: 'Part 1: A number minus itself equals the number before',
    example: '8 - 1 = 7',
    operation: 'subtract',
  },
  8: {
    // The shipped artwork for this frame is a literal placeholder - it reads
    // "Subtract...text needed". Copied verbatim rather than invented; the
    // client needs to supply the real wording.
    title: 'VIFs Subtract',
    subtitle: 'MIRROR Rule',
    body: 'Subtract...text needed',
    operation: 'subtract',
  },
  9: {
    title: 'VIFs Subtract',
    subtitle: 'SD from DD',
    body: 'Subtract a single digit number from a double digit number',
    operation: 'subtract',
  },
  10: {
    title: 'VIFs Subtract',
    subtitle: 'Tens Place',
    body: 'Subtract in the tens place',
    operation: 'subtract',
  },
  11: {
    title: 'VIFs Subtract',
    subtitle: 'Double Digits',
    body: "One's value of minuend is greater than subtrahend",
    operation: 'subtract',
  },
  12: {
    title: 'VIFs Subtract',
    subtitle: 'Double Digits',
    body: 'Subtract any two double-digit numbers',
    operation: 'subtract',
  },
};
